import React, { useState } from "react";
import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import { LayoutDashboard, Package, Users, Wallet, ListChecks, BadgeIndianRupee, UserCircle, LogOut, Shield, Menu, X, Trophy, IdCard, Award, BookOpen, Building2, BriefcaseBusiness, HelpCircle, FileText, Megaphone, ScanSearch, ShoppingBag, Smartphone, Zap, Layers, Search, Plus, Banknote, User, CalendarClock, Calculator, CreditCard } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { CreceLogo } from "@/components/CreceLogo";
import NotificationBell from "@/components/NotificationBell";
import { useTour } from "@/components/ProductTour";
import { useAutoCompanyRequest } from "@/components/corporate/CorporateRequestCard";

const NAV = [
  { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/app/products", label: "Products", icon: Package, testId: "nav-products" },
  { to: "/app/leads", label: "My Leads", icon: ListChecks, testId: "nav-leads" },
  { to: "/app/growth-exchange", label: "Growth Exchange", icon: ShoppingBag, testId: "nav-growth-exchange" },
  { to: "/app/daily-advance", label: "Daily Advance", icon: Zap, testId: "nav-daily-advance" },
  { to: "/app/bulk-business", label: "Bulk Business", icon: Layers, testId: "nav-bulk-business" },
  { to: "/app/quotes", label: "Offline Quotes", icon: FileText, testId: "nav-quotes" },
  { to: "/app/claims", label: "Claims", icon: Shield, testId: "nav-claims" },
  { to: "/app/policy-lens", label: "PolicyLens", icon: ScanSearch, testId: "nav-policy-lens" },
  { to: "/app/card-lens", label: "Card Lens", icon: CreditCard, testId: "nav-card-lens" },
  { to: "/app/clients", label: "Clients", icon: BriefcaseBusiness, testId: "nav-clients" },
  { to: "/app/renewals", label: "Renewals", icon: CalendarClock, testId: "nav-renewals" },
  { to: "/app/marketing", label: "Marketing", icon: Megaphone, testId: "nav-marketing" },
  { to: "/app/team", label: "My Team", icon: Users, testId: "nav-team" },
  { to: "/app/earnings", label: "Earnings", icon: BadgeIndianRupee, testId: "nav-earnings" },
  { to: "/app/withdraw", label: "Withdraw", icon: Wallet, testId: "nav-withdraw" },
  { to: "/app/contests", label: "Contests", icon: Trophy, testId: "nav-contests" },
  { to: "/app/training", label: "Training", icon: BookOpen, testId: "nav-training" },
  { to: "/app/certificates", label: "Certificates", icon: Award, testId: "nav-certificates" },
  { to: "/card", label: "Digital Card", icon: IdCard, testId: "nav-card" },
  { to: "/app/profile", label: "Profile", icon: UserCircle, testId: "nav-profile" },
];

function Sidebar({ user, onLogout, onClose }) {
  // P&L Heads get their own slim nav — they don't get MLM commissions (those
  // are filtered out in the commission cascade) and earn only via their
  // configured profit-share %. They DO need to see Products (to brief their
  // team), Team (to recruit + manage), Certificates, the Digital Card and
  // Profile. They DON'T see Withdraw / Contests / Training (those are the
  // consultant career-path tabs and don't apply to P&L Heads).
  const isPL = user?.role === "pl_partner";
  const isSales = user?.role === "sales";
  const isEmployee = !!user?.corporate_id;
  const isCorporate = user?.account_type === "corporate";
  const navItems = isSales
    ? [
        { to: "/app/field", label: "Field Home", icon: LayoutDashboard, testId: "nav-field" },
        { to: "/app/field/attendance", label: "Attendance", icon: CalendarClock, testId: "nav-field-attendance" },
        { to: "/app/field/onboard", label: "Onboard Consultant", icon: Plus, testId: "nav-field-onboard" },
        { to: "/app/field/consultants", label: "My Consultants", icon: Users, testId: "nav-field-consultants" },
        { to: "/app/field/scoreboard", label: "Scoreboard", icon: Trophy, testId: "nav-field-scoreboard" },
      ]
    : isPL
    ? [
        { to: "/app/products", label: "Products", icon: Package, testId: "nav-products" },
        { to: "/app/team", label: "My Team", icon: Users, testId: "nav-team" },
        { to: "/app/certificates", label: "Certificates", icon: Award, testId: "nav-certificates" },
        { to: "/card", label: "Digital Card", icon: IdCard, testId: "nav-card" },
        { to: "/app/profile", label: "Profile", icon: UserCircle, testId: "nav-profile" },
      ]
    : isEmployee
      ? NAV.filter((n) => !EMPLOYEE_HIDE.has(n.to))
      : isCorporate
        ? [...NAV.slice(0, 3), { to: "/app/company", label: "My Company", icon: Building2, testId: "nav-company" }, ...NAV.slice(3)]
        : NAV;
  const extra = [];
  if (!isPL && !isEmployee && !isSales && (user?.rank_index || 0) >= 4) extra.push({ to: "/app/my-branch", label: "My Branch", icon: Building2, testId: "nav-my-branch" });
  if (user?.role === "admin" || user?.role === "ceo" || user?.role === "cxo") extra.push({ to: "/app/admin", label: user?.role === "ceo" ? "CEO Panel" : user?.role === "cxo" ? "Executive Panel" : "Admin Panel", icon: Shield, testId: "nav-admin", accent: true });
  if (user?.role === "pl_partner") extra.push({ to: "/app/pl", label: "Profit Share", icon: Shield, testId: "nav-pl" });
  const item = (n) => {
    const Icon = n.icon;
    return (
      <NavLink key={n.to} to={n.to} data-testid={n.testId} onClick={onClose} title={n.label}
        className={({ isActive }) => `group/item flex flex-col lg:items-center items-start lg:gap-1 gap-3 lg:flex-col flex-row rounded-2xl px-3 py-2.5 text-[10px] lg:text-[10px] font-bold transition-all ${
          isActive ? (n.accent ? "bg-saffron-500 text-emerald-950 shadow-[0_8px_24px_-10px_rgba(251,191,36,.85)]" : "bg-emerald-900 text-white shadow-[0_10px_30px_-12px_rgba(2,44,34,.7)]") : (n.accent ? "text-saffron-600 hover:bg-saffron-50" : "text-stone-500 hover:bg-white hover:text-emerald-900 hover:shadow-sm")
        }`}>
        <Icon className="w-5 h-5 shrink-0" />
        <span className="lg:text-center leading-tight text-sm lg:text-[10px] lg:tracking-tight">{n.label}</span>
      </NavLink>
    );
  };
  return (
    <>
      <Link to="/app/dashboard" data-testid="sidebar-logo-link" className="flex lg:flex-col items-center gap-2 px-4 py-5">
        <CreceLogo size="md" theme="dark" />
        <span className="font-display font-black text-lg tracking-tight lg:hidden">Crece</span>
      </Link>
      <div className="px-2 flex-1 min-h-0 overflow-y-auto no-scrollbar pb-4">
        <div className="space-y-1">{navItems.map(item)}{extra.map(item)}</div>
      </div>
      <div className="p-2 shrink-0 border-t border-stone-200/70">
        <div className="px-2 py-2 lg:hidden">
          <div className="text-xs text-stone-500">Signed in as</div>
          <div className="font-semibold truncate" data-testid="sidebar-username">{user?.name}</div>
          <div className="text-xs text-stone-500 font-mono">+91 {user?.mobile}</div>
        </div>
        <button data-testid="logout-button" onClick={onLogout} title="Logout"
          className="w-full flex lg:flex-col items-center gap-2 lg:gap-1 rounded-2xl px-3 py-2.5 text-[10px] font-bold text-stone-500 hover:bg-rose-50 hover:text-rose-700 transition-colors">
          <LogOut className="w-5 h-5" /> <span className="text-sm lg:text-[10px]">Logout</span>
        </button>
      </div>
    </>
  );
}

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  useAutoCompanyRequest(user);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = () => { logout(); navigate("/"); };
  const close = () => setOpen(false);

  const [q, setQ] = useState("");
  const search = (e) => { e.preventDefault(); if (q.trim()) { navigate(`/app/leads?q=${encodeURIComponent(q.trim())}`); setQ(""); } };
  return (
    <div className="min-h-screen bg-[#FAFAF9] flex">
      <aside className="hidden lg:flex w-[88px] bg-[#F4F4F3] border-r border-stone-200/70 flex-col fixed inset-y-0 z-30">
        <Sidebar user={user} onLogout={handleLogout} onClose={close} />
      </aside>
      {open && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-stone-900/50" onClick={close} />
          <aside className="relative w-72 h-full bg-white flex flex-col">
            <button onClick={close} className="absolute top-4 right-4 p-1.5 hover:bg-stone-100 rounded"><X className="w-5 h-5" /></button>
            <Sidebar user={user} onLogout={handleLogout} onClose={close} />
          </aside>
        </div>
      )}
      <main className="flex-1 lg:ml-[88px] min-w-0">
        <header className="sticky top-0 z-20 px-4 lg:px-10 pt-4">
          <div className="glass border border-white/60 shadow-[0_8px_30px_-12px_rgba(2,44,34,.25)] rounded-full px-3 py-2 flex items-center gap-2 max-w-7xl" data-testid="topbar">
            <button onClick={() => setOpen(true)} data-testid="mobile-menu-button" className="lg:hidden p-2 hover:bg-stone-100 rounded-full"><Menu className="w-5 h-5" /></button>
            <div className="flex items-center gap-2 lg:hidden"><CreceLogo size="sm" theme="dark" /><span className="font-display font-black">Crece</span></div>
            <form onSubmit={search} className="hidden sm:flex flex-1 items-center gap-2 rounded-full bg-stone-100/80 px-4 py-2 ml-1">
              <Search className="w-4 h-4 text-stone-400" />
              <input data-testid="topbar-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search leads by ref, name or mobile…" className="bg-transparent outline-none text-sm w-full placeholder:text-stone-400" />
            </form>
            <div className="flex-1 sm:hidden" />
            <TourTriggerButton />
            <NotificationBell />
            <Link to="/app/profile" data-testid="topbar-profile" className="hidden sm:flex items-center gap-2 rounded-full bg-emerald-900 text-white pl-1 pr-3 py-1 ml-1">
              <span className="w-7 h-7 rounded-full bg-saffron-400 text-emerald-950 grid place-items-center text-xs font-black">{(user?.name || "?").slice(0, 1).toUpperCase()}</span>
              <span className="text-xs font-bold max-w-[120px] truncate" data-testid="sidebar-username-top">{user?.name}</span>
            </Link>
          </div>
        </header>
        <div className="px-4 lg:px-10 py-8 lg:py-10 pb-28 lg:pb-10 max-w-7xl stagger">
          <Outlet />
        </div>
        <MobileDock user={user} />
      </main>
    </div>
  );
}

const DOCK = [
  { to: "/app/products", label: "New Lead", icon: Plus, testId: "dock-new-lead", primary: true },
  { to: "/app/daily-advance", label: "Advance", icon: Zap, testId: "dock-daily-advance" },
  { to: "/app/earnings", label: "Earnings", icon: Banknote, testId: "dock-earnings" },
  { to: "/app/profile", label: "Profile", icon: User, testId: "dock-profile" },
];

const EMPLOYEE_HIDE = new Set(["/app/daily-advance", "/app/bulk-business", "/app/growth-exchange", "/app/marketing", "/app/team", "/app/earnings", "/app/withdraw", "/app/contests", "/app/certificates", "/card"]);

function MobileDock({ user }) {
  const isEmployee = !!user?.corporate_id;
  const items = DOCK.filter((d) => !(isEmployee && EMPLOYEE_HIDE.has(d.to)));
  return (
    <nav className="lg:hidden fixed bottom-3 inset-x-3 z-30" data-testid="mobile-dock">
      <div className="glass border border-white/70 shadow-[0_12px_40px_-12px_rgba(2,44,34,.45)] rounded-full px-2 py-1.5 grid" style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}>
        {items.map((d) => {
          const Icon = d.icon;
          return (
            <NavLink key={d.to} to={d.to} data-testid={d.testId} className={({ isActive }) => `flex flex-col items-center gap-0.5 rounded-full py-1.5 text-[10px] font-bold transition-colors ${d.primary ? "text-emerald-950" : isActive ? "text-emerald-900" : "text-stone-500"}`}>
              <span className={`w-9 h-9 rounded-full grid place-items-center ${d.primary ? "bg-saffron-500 shadow-[0_8px_20px_-8px_rgba(251,191,36,.9)]" : ""}`}><Icon className="w-5 h-5" /></span>
              {d.label}
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
}

/** Small (?) icon in the topbar — replays the product tour for the current role. */
function TourTriggerButton() {
  const { start } = useTour();
  return (
    <button
      type="button"
      onClick={start}
      title="Take the product tour"
      data-testid="tour-trigger-topbar"
      className="p-2 rounded-full hover:bg-stone-100 text-stone-600 hover:text-emerald-900 transition-colors">
      <HelpCircle className="w-5 h-5" />
    </button>
  );
}
